import Grid from '@mui/material/Grid';
import MixturesDetailPanel from 'components/DetailPanels/MixturesDetailPanel';
import ProductsDetailPanel from 'components/DetailPanels/ProductsDetailPanel';
import ShipmentsDetailPanel from 'components/DetailPanels/ShipmentsDetailPanel';
import BaseTable from 'components/Tables/BaseTable';
import cloneDeep from 'lodash/cloneDeep';
import orderBy from 'lodash/orderBy';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import docDefEchaLinked from 'reports/inventory/echaRawMaterialsLinked';
import docDefEchaNotLinked from 'reports/inventory/echaRawMaterialsNotLinked';
import { createSelector } from 'reselect';
import { getImmutableStore, groupBy, mixtureSubstancesBySubstance, shipmentsBySubstance } from 'selectors/generic';
import theme from 'source/theme';
import arrayToObject from 'utils/arrayToObject';
import { SUBSTANCE } from 'utils/constants/coreConstants';
import { matchEchaRawMaterial } from 'utils/echa/match';
import tableIcons from 'utils/icons/materialTableIcons';
import columns from 'utils/tables/columns/echaStatus';
import EchaLinkedTable from './EchaLinkedDetailPanel';

pdfMake.addVirtualFileSystem(pdfFonts);

const Component = props => {
    const {
        echas, mixturesBySubstanceId, productIdsByRawMaterialId, productsById,
        rawMaterialsById, shipmentsBySubstanceId, substances, suppliersById
    } = props;
    const tableRef = React.useRef(null);

    const [matches, setMatches] = useState({});
    const [filterEcha, setFilterEcha] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const tempMatches = matchEchaRawMaterial(substances, echas.data);
        setMatches(tempMatches);
        setLoading(false);
    }, []);

    const matchsArray = Object.keys(matches);

    // The substance's own shipments followed by the shipments of every mixture
    // it is part of - the same concatenation this built by hand out of
    // `rm.raw_material_suppliers` and `rm.mixtures`, read off an index now.
    // Only the count is used here, on the Fornecimentos panel.
    const rawMaterialShipments = rawMaterial => shipmentsBySubstanceId[rawMaterial.id] || [];

    const reportDataEchaLinked = () => {
        let filteredTableData = cloneDeep(tableRef.current.state.data);
        //let filteredTableData = cloneDeep(substances);
        const filteredTableDataObj = arrayToObject(filteredTableData);
        matchsArray.forEach(substanceId =>
            matches[substanceId].forEach(echaId => {
                if (filteredTableDataObj[substanceId]) {
                    if (filteredTableDataObj[substanceId].echas) {
                        filteredTableDataObj[substanceId]['echas'].push(echas.models[echaId]);
                    } else {
                        filteredTableDataObj[substanceId]['echas'] = [echas.models[echaId]];
                    }
                }
            })
        );
        return Object.values(filteredTableDataObj);
    };


    const reportDataEchaNotLinked = echas.data.filter(item =>
        (item.ec.length === 0 || (item.ec.length === 1 && item.ec[0].length === 0)) &&
        (item.cas.length === 0 || (item.cas.length === 1 && item.cas[0].length === 0))
    );

    const substancesToRender = () => {
        if (filterEcha) {
            const substancesObj = arrayToObject(substances);
            return orderBy(
                matchsArray.map(substanceId => ({
                    ...substancesObj[substanceId],
                    link_echa: matches[substanceId].length
                })),
                ['name'],
                ['asc']
            );
        } else {
            return substances.map(substance => matches[substance.id] ?
                { ...substance, link_echa: matches[substance.id].length } :
                substance
            );
        }
    };

    return (
        <Grid size={12}>
            <BaseTable
                ref={tableRef}
                columns={columns}
                onRowClick={() => null}
                data={substancesToRender()}
                order={false}
                isLoading={loading}
                actions={[
                    {
                        isFreeAction: true,
                        icon: filterEcha ? tableIcons.FilterWrongStyled : tableIcons.FilterWrong,
                        tooltip: 'Filtrar ECHAs ligadas',
                        onClick: (event, rowData) => {
                            setFilterEcha(!filterEcha);
                        }
                    },
                    {
                        isFreeAction: true,
                        icon: tableIcons.Pdf,
                        tooltip: 'Relatório PDF',
                        onClick: (event, rowData) => {
                            pdfMake.createPdf(docDefEchaLinked(reportDataEchaLinked(), rawMaterialsById)).open();
                        }
                    },
                    {
                        isFreeAction: true,
                        icon: tableIcons.Pdf2,
                        tooltip: 'Relatório ECHAs NÃO ligadas',
                        onClick: (event, rowData) => {
                            pdfMake.createPdf(docDefEchaNotLinked(reportDataEchaNotLinked)).open();
                        }
                    }
                ]}
                detailPanel={[
                    rowData => {
                        const linkedEcha = matchsArray.includes(rowData.id.toString());
                        return linkedEcha ?
                            {
                                icon: tableIcons.EchaLinkStyledOk,
                                tooltip: 'Ver ECHAs ligadas',
                                disabled: false,
                                render: () => <EchaLinkedTable rowData={matches[rowData.id]} data={echas.models}
                                                               title={'ECHAs ligadas'}/>
                            } :
                            {
                                icon: tableIcons.EchaLink,
                                disabled: true,
                                render: () => null
                            };
                    },
                    rowData => ({
                        disabled: rawMaterialShipments(rowData).length === 0,
                        icon: tableIcons.Shipments,
                        tooltip: rawMaterialShipments(rowData).length === 0 ? '' : 'Fornecimentos',
                        render: () => <ShipmentsDetailPanel rowData={rowData} suppliersById={suppliersById}
                                                            rawMaterialsById={rawMaterialsById}
                                                            title={'Fornecimentos'}/>

                    }),
                    rowData => {
                        const mixtures = mixturesBySubstanceId[rowData.id] || [];
                        return {
                            disabled: mixtures.length === 0,
                            icon: tableIcons.Mixtures,
                            tooltip: mixtures.length === 0 ? '' : 'Misturas',
                            render: () => <MixturesDetailPanel rowData={mixtures} data={rawMaterialsById}
                                                               title={'Misturas'}/>
                        };
                    },
                    rowData => {
                        const products = productIdsByRawMaterialId[rowData.id] || [];
                        return {
                            icon: products.length > 0 ?
                                tableIcons.Products :
                                tableIcons.ProductsStyled({ htmlColor: theme.palette.error.main }),
                            tooltip: 'Produtos',
                            render: () => <ProductsDetailPanel rowData={products} data={productsById}
                                                               title={'Produtos'}/>
                        };
                    }
                ]}
            />
        </Grid>
    );
};

const makeMapStateToProps = () => {
    // Was a filterByFunctionSelector, which hands the predicate one row and
    // nothing else - and half of this predicate is no longer on the row.
    // `rm.mixtures` is its own slice, so the index becomes a second input and
    // the clone still happens once per slice change rather than once a render.
    const filteredRawMaterials = createSelector(
        [state => state.rawMaterials.data, mixtureSubstancesBySubstance],
        (rawMaterials, mixturesOf) => cloneDeep(
            rawMaterials.filter(rm => rm.type === SUBSTANCE &&
                ((rm.code !== null && rm.code !== '') || (mixturesOf[rm.id] || []).length > 0))
        )
    );
    // `rm.products` - the ids ProductsDetailPanel looks up. The M2M sent one id
    // per composition row, so a raw material that enters a product twice - once
    // directly, once through a mixture - was sent twice. Reproduced as it
    // arrived, duplicates included; the panel is what de-duplicates, and the
    // Produtos icon only asks whether the list is empty.
    const productIdsByRawMaterial = createSelector(
        [groupBy('productRawMaterials', 'raw_material_id')],
        composition => Object.keys(composition).reduce((acc, rawMaterialId) => {
            acc[rawMaterialId] = composition[rawMaterialId].map(row => row.product_id);
            return acc;
        }, {})
    );
    const immutableEchas = getImmutableStore('echa');
    const mapStateToProps = (state, props) => ({
        echas: immutableEchas(state),
        mixturesBySubstanceId: mixtureSubstancesBySubstance(state),
        productIdsByRawMaterialId: productIdsByRawMaterial(state),
        productsById: state.products.models,
        rawMaterialsById: state.rawMaterials.models,
        shipmentsBySubstanceId: shipmentsBySubstance(state),
        substances: filteredRawMaterials(state),
        suppliersById: state.suppliers.models
    });
    return mapStateToProps;
};

export default connect(makeMapStateToProps)(Component);
